import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AuthenticationService } from '../base/authentication.service';
import { User } from '../../interfaces/routering/user';
import { environment } from '../../../environments/environment';
import { Report } from '../../interfaces/report/report';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { ApiService } from '../base/api.service';
import { ServerRouteProperties } from '../../interfaces/base/server-route-properties';
import * as _ from 'lodash';
import { ServerResponse } from '../../interfaces/base/server.response';
import { ReportService } from './report.service';
import { take } from 'rxjs/operators';
import { Organisation } from '../../interfaces/routering/organisation';
import { Router } from '@angular/router';
import { BroadcastService } from '../base/broadcast.service';
import { WebSocketResponse } from '../../interfaces/base/web-socket-response';
import { Department } from '../../interfaces/routering/department';

@Injectable()

export class ReportWebhookService {

  public reportUpdated: Subject<Report> = new Subject<Report>();
  public reportDeleted: Subject<Report> = new Subject<Report>();
  public reportCount: Subject<'add' | 'add-review' | 'remove'> = new Subject<'add' | 'add-review' | 'remove'>();

  private organisation: Organisation = null;

  private user: User = null;
  private routeParams: ServerRouteProperties = {
    active: '',
    direction: '',
    filters: [],
  };

  private clearBroadcastEvents: string[] = [];

  constructor(private authenticationService: AuthenticationService,
              private api: ApiService,
              private reportService: ReportService,
              private router: Router,
              private broadcastService: BroadcastService,
  ) {
    this.authenticationService.organisation$.subscribe((organisation: Organisation): void => {
      this.organisation = organisation;
    });

    this.reportCount.subscribe((a: 'add' | 'add-review' | 'remove'): void => {
      if (a === 'add') {
        this.api.length++;
        this.api.matPaginator.length = this.api.length;
      } else if (a === 'remove' || a === 'add-review') {
        this.api.length--;
        this.api.matPaginator.length = this.api.length;
      }
    });

    this.api.preSetFilters.pipe(take(1)).subscribe((): void => {
      if (this.api.filterPageName === 'reports-open') {
        this.routeParams = JSON.parse(JSON.stringify(this.api.routeParams));
      }
    });

    this.api.filterUpdated.subscribe((): void => {
      if (this.api.filterPageName === 'reports-open') {
        this.routeParams = JSON.parse(JSON.stringify(this.api.routeParams));
      }
    });
  }

  init(dataSource: MatTableDataSource<Report> = null, paginator: MatPaginator): void {
    this.authenticationService.user$.subscribe((user: User): void => {
      if (user) {
        const i: number = 0;
        const interval: NodeJS.Timeout = setInterval((): void => {
          if (typeof window.Echo !== 'undefined') {
            if (!environment.production || this.user && this.user.role === 'super-admin') {
              console.log('ReportWebhookService ready');
            }

            this.destroy();

            this.clearBroadcastEvents.push('report.created');
            this.broadcastService.getEventSubject('report', 'created').subscribe((data: WebSocketResponse<Report>): void => {
              this.processData(user, paginator, dataSource, data.data, 'created');
            });

            this.clearBroadcastEvents.push('report.updated');
            this.broadcastService.getEventSubject('report', 'updated').subscribe((data: WebSocketResponse<Report>): void => {
              this.processData(user, paginator, dataSource, data.data, 'updated');
            });

            this.clearBroadcastEvents.push('report.rejected');
            this.broadcastService.getEventSubject('report', 'rejected').subscribe((data: WebSocketResponse<Report>): void => {
              this.processData(user, paginator, dataSource, data.data, 'rejected');
            });

            this.clearBroadcastEvents.push('report.deleted');
            this.broadcastService.getEventSubject('report', 'deleted').subscribe((data: WebSocketResponse<Report>): void => {
              this.processData(user, paginator, dataSource, data.data, 'deleted');
            });

            this.clearBroadcastEvents.push('report.in-review');
            this.broadcastService.getEventSubject('report', 'in-review').subscribe((data: WebSocketResponse<Report>): void => {
              this.processData(user, paginator, dataSource, data.data, 'in-review');
            });

            this.clearBroadcastEvents.push('report.reopened');
            this.broadcastService.getEventSubject('report', 'reopened').subscribe((data: WebSocketResponse<Report>): void => {
              this.processData(user, paginator, dataSource, data.data, 'reopened');
            });

            this.clearBroadcastEvents.push('report.resolved');
            this.broadcastService.getEventSubject('report', 'resolved').subscribe((data: WebSocketResponse<Report>): void => {
              this.processData(user, paginator, dataSource, data.data, 'resolved');
            });

            clearInterval(interval);
          } else {
            console.log('ReportWebhookService not ready');
          }
          if (i > 100) {
            clearInterval(interval);
          }
        }, 100);
      }
    });
  }

  processData(user: User, paginator: MatPaginator, dataSource: MatTableDataSource<Report>, report: Report, type: 'created' | 'updated' | 'resolved' | 'rejected' | 'reopened' | 'deleted' | 'in-review'): void {
    if (!environment.production || this.user && this.user.role === 'super-admin') {
      console.log(this.routeParams);
    }

    const canReportBeAdded: boolean = this.canReportBeAdded(dataSource, report);
    if (!environment.production || this.user && this.user.role === 'super-admin') {
      console.log('canReportBeAdded:', canReportBeAdded);
    }

    // add new report to appropriate user(s)
    if (type === 'created' && paginator.pageIndex === 0 && this.canReportBeAddedForCurrentUser(user, report)) {
      // check if report can even be added because of filters
      if (canReportBeAdded && this.canReportBeAddedOnCurrentPage(dataSource, report)) {
        const index: number = dataSource.data.findIndex((d: Report): boolean => d.id === report.id);
        if (index === -1) {
          if (this.canUpdateCounting()) {
            this.reportCount.next('add');
          }

          // if enough reports, remove last one
          if (dataSource.data.length >= this.api.pageSize) {
            if (!environment.production || this.user && this.user.role === 'super-admin') {
              console.log('removing last report from list');
            }
            dataSource.data.splice(-1, 1);
          }

          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('adding report to the list');
          }
          dataSource.data.push(report);
        }
      }
    }

    // update existing report
    if ((type === 'updated' || type === 'reopened')) {
      this.reportUpdated.next(report);

      const index: number = dataSource.data.findIndex((d: Report): boolean => d.id === report.id);
      if (this.canReportBeAddedForCurrentUser(user, report)) {
        // if report exists, update it
        if (index !== -1) {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('report found, replace the data');
          }
          dataSource.data[index] = report;
        } else {
          // report not found, add it
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('report not found, adding to the list');
          }

          if (this.canUpdateCounting()) {
            this.reportCount.next('add');
          }

          dataSource.data.push(report);
        }
      } else {
        if (index !== -1) {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('report found, remove it');
          }
          dataSource.data.splice(index, 1);

          if (this.canUpdateCounting()) {
            this.reportCount.next('remove');
          }

          this.getMissingData(dataSource);
        }
      }
    }

    // remove processed report from list
    if (['resolved', 'rejected'].indexOf(type) !== -1) {
      const index: number = dataSource.data.findIndex((d: Report): boolean => d.id === report.id);
      if (this.canReportBeAddedForCurrentUser(user, report)) {
        // report not found, add it
        if (!environment.production || this.user && this.user.role === 'super-admin') {
          console.log('report not found, adding to the list');
        }

        if (index !== -1) {
          dataSource.data[index] = report;
        } else {
          if (this.canUpdateCounting()) {
            this.reportCount.next('add');
          }

          dataSource.data.push(report);
        }
      } else {
        if (index !== -1) {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('report found, remove it');
          }
          dataSource.data.splice(index, 1);

          if (this.canUpdateCounting()) {
            this.reportCount.next('remove');
          }

          this.getMissingData(dataSource);
        }
      }
    }

    if (['in-review'].indexOf(type) !== -1) {
      const index: number = dataSource.data.findIndex((d: Report): boolean => d.id === report.id);
      if (this.canReportBeAddedForCurrentUser(user, report)) {
        // report not found, add it
        if (!environment.production || this.user && this.user.role === 'super-admin') {
          console.log('report not found, adding to the list');
        }

        if (this.canUpdateCounting()) {
          this.reportCount.next('add-review');
        }

        if (index !== -1) {
          dataSource.data[index] = report;
        } else {
          if (this.canUpdateCounting()) {
            this.reportCount.next('add');
          }

          dataSource.data.push(report);
        }
      } else {
        if (index !== -1) {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('report found, remove it');
          }
          dataSource.data.splice(index, 1);

          if (this.canUpdateCounting()) {
            this.reportCount.next('remove');
          }

          this.getMissingData(dataSource);
        }
      }
    }

    // delete existing report
    if (type === 'deleted') {
      // report can be seen by current user
      if (user.permissions.includes('reports.deleted.read')) {
        const index: number = dataSource.data.findIndex((d: Report): boolean => d.id === report.id);
        if (index !== -1) {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('report found, replace the data');
          }
          // report deleted second time, presume force-delete and remove it from the list
          if (typeof dataSource.data[index].is_deleted !== 'undefined' && dataSource.data[index].is_deleted === true) {
            dataSource.data.splice(index, 1);

            if (this.canUpdateCounting()) {
              this.reportCount.next('remove');
            }

            this.getMissingData(dataSource);
          } else {
            // report deleted first time, update it
            dataSource.data[index] = report;
          }
        }

        this.reportDeleted.next(report);
      } else {
        // report can't be seen by current user
        const index: number = dataSource.data.findIndex((d: Report): boolean => d.id === report.id);
        if (index !== -1) {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('report found, remove it');
          }
          dataSource.data.splice(index, 1);

          if (this.canUpdateCounting()) {
            this.reportCount.next('remove');
          }

          this.getMissingData(dataSource);
        }
      }
    }

    if (canReportBeAdded) {
      dataSource.data.sort((a: Report, b: Report): number => {
        let a1: string | number = null,
          b1: string | number = null,
          sortByReceivedAt: boolean = false;
        if (this.routeParams.active === 'created_at' || this.routeParams.active === '' || this.routeParams.active === null) {
          a1 = a.created_at;
          b1 = b.created_at;
        } else if (this.routeParams.active === 'number') {
          a1 = parseInt(a[this.routeParams.active].replace(this.organisation.name, '').replace('-', ''), 10);
          b1 = parseInt(b[this.routeParams.active].replace(this.organisation.name, '').replace('-', ''), 10);
        } else if (this.routeParams.active === 'complaint_location') {
          a1 = _.get(a, 'complaint.streetname', null);
          a1 = (!_.isEmpty(a1) ? a1 + ' ' + _.get(a, 'complaint.house_number', null) + ' ' + _.get(a, 'complaint.house_number_addition', null) : a1);
          b1 = _.get(b, 'complaint.streetname', null);
          b1 = (!_.isEmpty(b1) ? b1 + ' ' + _.get(b, 'complaint.house_number', null) + ' ' + _.get(b, 'complaint.house_number_addition', null) : b1);
        } else if (this.routeParams.active === 'head_category_id') {
          a1 = _.get(a, 'categories[0].head_category.name', null);
          b1 = _.get(b, 'categories[0].head_category.name', null);
          sortByReceivedAt = true;
        } else if (this.routeParams.active === 'sub_category_id') {
          a1 = _.get(a, 'categories[0].sub_category.name', null);
          b1 = _.get(b, 'categories[0].sub_category.name', null);
          sortByReceivedAt = true;
        } else if (this.routeParams.active === 'assigned_to') {
          a1 = a[this.routeParams.active].name.toUpperCase();
          b1 = b[this.routeParams.active].name.toUpperCase();
          sortByReceivedAt = true;
        } else if (this.routeParams.active === 'status_id') {
          a1 = _.get(a, 'status.display_name', null);
          b1 = _.get(b, 'status.display_name', null);
          sortByReceivedAt = true;
        } else if (this.routeParams.active === 'response_days') {
          a1 = _.get(a, 'handling.response_days', null);
          b1 = _.get(b, 'handling.response_days', null);
        }

        if (sortByReceivedAt) {
          if (this.routeParams.direction === 'desc' || this.routeParams.direction === '' || this.routeParams.direction === null) {
            return (b1 < a1 ? -1 : (b1 > a1 ? 1 : (b.created_at < a.created_at ? -1 : (b.created_at > a.created_at ? 1 : 0))));
          }

          return (a1 < b1 ? -1 : (a1 > b1 ? 1 : (b.created_at < a.created_at ? -1 : (b.created_at > a.created_at ? 1 : 0))));
        } else {
          if (this.routeParams.direction === 'desc' || this.routeParams.direction === '' || this.routeParams.direction === null) {
            return (b1 < a1 ? -1 : (b1 > a1 ? 1 : 0));
          }

          return (a1 < b1 ? -1 : (a1 > b1 ? 1 : 0));
        }
      });

      dataSource.data = dataSource.data;
    }
  }

  canReportBeAdded(dataSource: MatTableDataSource<Report>, report: Report): boolean {
    const last: Report = _.last(dataSource.data);
    let response: boolean = true;

    // check if sorting is applied
    // if applied to text columns, get start and end 'cut-off' strings
    // check if report falls between those strings
    if (response) {
      if (typeof this.routeParams.active !== 'undefined' && this.routeParams.active !== null && this.routeParams.active !== '') {
        if (this.routeParams.active === 'created_at' && (
          this.routeParams.direction === 'asc' && report.created_at > _.get(last, 'created_at', null) && dataSource.data.length >= this.api.pageSize
        )) {
          response = false;
        }
        if (this.routeParams.active === 'number' && typeof last !== 'undefined' && (
          this.routeParams.direction === 'asc' && parseInt(report.number.replace(this.organisation.name, '').replace('-', ''), 10) > parseInt(last.number.replace(this.organisation.name, '').replace('-', ''), 10)
        )) {
          response = false;
        }
        if (this.routeParams.active === 'complaint_location' && typeof report.complaint.streetname !== 'undefined' && (
          this.routeParams.direction === 'desc' && _.get(report, 'complaint.streetname', null) < _.get(last, 'complaint.streetname', null) ||
          this.routeParams.direction === 'asc' && _.get(report, 'complaint.streetname', null) > _.get(last, 'complaint.streetname', null)
        )) {
          response = false;
        }
        if (this.routeParams.active === 'head_category_id' && _.get(report, 'head_category.name', null) > _.get(last, 'head_category.name', null)) {
          response = false;
        }
        if (this.routeParams.active === 'sub_category_id' && _.get(report, 'sub_category.name', null) > _.get(last, 'sub_category.name', null)) {
          response = false;
        }
        if (this.routeParams.active === 'assigned_to' && (
          this.routeParams.direction === 'desc' && _.get(report, 'assigned_to') < _.get(last, 'assigned_to') ||
          this.routeParams.direction === 'asc' && _.get(report, 'assigned_to') > _.get(last, 'assigned_to')
        )) {
          response = false;
        }
        if (this.routeParams.active === 'status_id' && (
          this.routeParams.direction === 'desc' && _.get(report, 'status.display_name') < _.get(last, 'status.display_name') ||
          this.routeParams.direction === 'asc' && _.get(report, 'status.display_name') > _.get(last, 'status.display_name')
        )) {
          response = false;
        }
      }
      if (!environment.production || this.user && this.user.role === 'super-admin') {
        console.log('sorting judgement: ', response);
      }
    }

    // check if filtering is applied
    // if applied, check start and end cut-off strings
    if (response && this.routeParams.filters.length) {
      this.routeParams.filters.forEach(filter => {
        if (filter.field === 'created_at') {
          const date: string[] = (<string>filter.value).split('!');
          if (date[0] > report.created_at || date[1] < report.created_at) {
            response = false;
          }
        }
        if (filter.field === 'number' && report.number.indexOf(filter.value.toString()) !== 0) {
          response = false;
        }
        if (filter.field === 'complaint_location' && (
          _.defaultTo(_.get(report, 'complaint.streetname'), '').toLowerCase().indexOf((<string>filter.value).toLowerCase()) === -1 &&
          _.defaultTo(_.get(report, 'complaint.postalcode'), '').toLowerCase().indexOf((<string>filter.value).toLowerCase()) === -1 &&
          _.defaultTo(_.get(report, 'complaint.house_number_with_addition'), '').toLowerCase().indexOf((<string>filter.value).toLowerCase()) === -1 &&
          _.defaultTo(_.get(report, 'complaint.city'), '').toLowerCase().indexOf((<string>filter.value).toLowerCase()) === -1 &&
          (
            _.defaultTo(_.get(report, 'complaint.postalcode'), '').toLowerCase() + ' ' +
            _.defaultTo(_.get(report, 'complaint.house_number_with_addition'), '')
          ).indexOf((<string>filter.value).toLowerCase()) === -1 &&
          (
            _.defaultTo(_.get(report, 'complaint.streetname'), '').toLowerCase() + ' ' +
            _.defaultTo(_.get(report, 'complaint.house_number_with_addition'), '')
          ).indexOf((<string>filter.value).toLowerCase()) === -1
        )) {
          response = false;
        }
        if (filter.field === 'head_category_id' && _.get(report, 'head_category_id', null) !== filter.value) {
          response = false;
        }
        if (filter.field === 'sub_category_id' && _.get(report, 'sub_category_id', null) !== filter.value) {
          response = false;
        }
        if (filter.field === 'assigned_to' && (filter.value === 'none' && report.assigned_id !== null || filter.value !== 'none' && report.assigned_id !== filter.value)) {
          response = false;
        }
        if (filter.field === 'status_id' && report.status_id !== filter.value) {
          response = false;
        }
      });
    }
    if (!environment.production || this.user && this.user.role === 'super-admin') {
      console.log('filters judgement: ', response);
    }

    return response;
  }

  canReportBeAddedOnCurrentPage(dataSource: MatTableDataSource<Report>, report: Report): boolean {
    const first: Report = _.first(dataSource.data),
      last: Report = _.last(dataSource.data);
    let response: boolean = true;

    if (!environment.production || this.user && this.user.role === 'super-admin') {
      console.log(this.routeParams);
      console.log(report, dataSource.data, last);
    }

    // check if report should be shown on current page
    if (response && typeof first !== 'undefined' && typeof last !== 'undefined') {
      if (this.routeParams.page === 1 || this.routeParams.page === null || typeof this.routeParams.page === 'undefined') {
        if (this.routeParams.direction === 'desc' || this.routeParams.direction === '' || this.routeParams.direction === null) {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('report: ', report.created_at);
            console.log('last: ', last.created_at);
          }
          if (last.created_at > report.created_at) {
            if (!environment.production || this.user && this.user.role === 'super-admin') {
              console.log('11');
            }
            response = false;
          }
        } else {
          if (!environment.production || this.user && this.user.role === 'super-admin') {
            console.log('first: ', first.created_at);
            console.log('report: ', report.created_at);
          }
          if (first.created_at < report.created_at) {
            if (!environment.production || this.user && this.user.role === 'super-admin') {
              console.log('12');
            }
            response = false;
          }
        }
      } else {
        if (!environment.production || this.user && this.user.role === 'super-admin') {
          console.log('first: ', first.created_at);
          console.log('report: ', report.created_at);
          console.log('last: ', last.created_at);
        }
        if (this.routeParams.direction === 'desc' || this.routeParams.direction === '' || this.routeParams.direction === null) {
          if (first.created_at < report.created_at || last.created_at > report.created_at) {
            if (!environment.production || this.user && this.user.role === 'super-admin') {
              console.log('13');
            }
            response = false;
          }
        } else {
          if (first.created_at > report.created_at || last.created_at < report.created_at) {
            if (!environment.production || this.user && this.user.role === 'super-admin') {
              console.log('14');
            }
            response = false;
          }
        }
      }
    }
    if (!environment.production || this.user && this.user.role === 'super-admin') {
      console.log('page judgement: ', response);
    }

    return response;
  }

  canReportBeAddedForCurrentUser(user: User, report: Report): boolean {
    return (
        this.router.url.indexOf('/meldingen/alle') !== -1 && user.permissions.some((p: string): boolean => ['reports.view.all'].includes(p))
      ) ||
      this.router.url.indexOf('/meldingen/gesloten') !== -1 && (
        user.permissions.some((p: string): boolean => ['reports.view'].includes(p)) &&
        ['resolved', 'rejected'].indexOf(report.status.name) !== -1 && (
          report.assigned_type === 'user' &&
          user.id === report.assigned_id
        ) ||
        (
          report.assigned_type === 'department' &&
          user.departments.some((d: Department): boolean => d.id === report.assigned_id)
        )
      ) ||
      (
        this.router.url.indexOf('/meldingen/alle') === -1 &&
        this.router.url.indexOf('/meldingen/controle') === -1 &&
        user.permissions.some((p: string): boolean => ['reports.view'].includes(p)) &&
        ['not-assigned', 'future', 'assigned', 'in-progress', 'reopened', 'review-failed'].indexOf(report.status.name) !== -1 && (
          (
            report.assigned_type === 'user' &&
            user.id === report.assigned_id
          ) ||
          (
            report.assigned_type === 'department' &&
            user.departments.some((d: Department): boolean => d.id === report.assigned_id)
          )
        )
      ) ||
      (
        this.router.url.indexOf('/meldingen/controle') !== -1 && (
          user.permissions.some((p: string): boolean => ['reports.review'].includes(p)) &&
          report.status.name === 'in-review'
        )
      );
  }

  canUpdateCounting(): boolean {
    return (
      this.router.url.indexOf('/meldingen/alle') === -1 &&
      this.router.url.indexOf('/meldingen/controle') === -1 &&
      this.router.url.indexOf('/meldingen/gesloten') === -1
    );
  }

  getMissingData(dataSource: MatTableDataSource<Report>): void {
    const start: number = dataSource.data.length,
      filterPageName: string = this.api.filterPageName;

    this.api.filterPageName = null;
    this.reportService.missing('open', start).subscribe((response: ServerResponse): void => {
      if (typeof response.data !== 'undefined' && typeof response.data[0] !== 'undefined') {
        dataSource.data.push(response.data[0]);

        dataSource.data = dataSource.data;

        this.reportCount.next('add');
      }

      this.api.filterPageName = filterPageName;
    });
  }

  clear(): void {
    this.reportUpdated.next(null);
    this.reportDeleted.next(null);
  }

  destroy(): void {
    this.clearBroadcastEvents.forEach((event: string): void => this.broadcastService.clearEventSubject(event));
  }
}
